import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { html as staticHtml, unsafeStatic } from 'lit/static-html.js';

import type { PanelRoute } from '../router.ts';
import { phase1Panels } from '../panels-registry.ts';

/**
 * `<exhibition-panel-view>` — central viewing area.
 *
 * Mounts the active panel as a real Lit custom element (`<panel-<id>>`)
 * via lit/static-html, sized by container queries so the panel scales
 * fluidly with the available space without re-layout. The dev overlays
 * (`i`, `g`, `r`) toggle on keystroke and are intentionally orthogonal.
 */
@customElement('exhibition-panel-view')
export class ExhibitionPanelViewElement extends LitElement {
  static override styles = [
    css`
      :host {
        display: grid;
        place-items: center;
        container-type: inline-size;
        padding: var(--space-xl, 1.5rem);
        background:
          radial-gradient(
            ellipse at 50% 0%,
            color-mix(in oklab, var(--chassis-colour, #1a1a1a) 50%, white) / 0.05 0%,
            transparent 60%
          ),
          var(--panel-bezel, #0c0c0d);
      }

      .stage {
        position: relative;
        inline-size: min(100%, 64rem);
        block-size: 100%;
        display: grid;
        place-items: center;
        isolation: isolate;
      }

      .stage > * {
        max-block-size: 100%;
        max-inline-size: 100%;
      }

      .stage[data-grid='true']::after {
        content: '';
        position: absolute;
        inset: 0;
        background-image:
          linear-gradient(to right, rgba(255, 255, 255, 0.06) 1px, transparent 1px),
          linear-gradient(to bottom, rgba(255, 255, 255, 0.06) 1px, transparent 1px);
        background-size: 5% 10%;
        pointer-events: none;
        z-index: 2;
      }

      .empty {
        font-family: var(--font-data, monospace);
        font-size: var(--type-small, 0.75rem);
        color: var(--text-secondary, #8a8a8a);
        text-align: center;
        padding-block: var(--space-xxl, 3rem);
      }

      .meta {
        position: absolute;
        inset-block-end: -1.4rem;
        inset-inline-start: 0;
        font-family: var(--font-data, monospace);
        font-size: var(--type-tiny, 0.6875rem);
        color: var(--text-secondary, #6a6a6a);
        letter-spacing: 0.06em;
      }
    `,
  ];

  @property({ attribute: false }) accessor route: PanelRoute | null = null;

  @state() private accessor overlayIds = false;
  @state() private accessor overlayGrid = false;
  @state() private accessor overlayReference = false;

  override connectedCallback(): void {
    super.connectedCallback();
    window.addEventListener('keydown', this.#onKey);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener('keydown', this.#onKey);
  }

  #onKey = (e: KeyboardEvent): void => {
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    // Don't hijack single-letter keys while the operator is typing into a
    // field — overlays are dev-affordances, not editor shortcuts.
    if (isTypingTarget(e.target)) return;
    switch (e.key) {
      case 'i':
        this.overlayIds = !this.overlayIds;
        break;
      case 'g':
        this.overlayGrid = !this.overlayGrid;
        break;
      case 'r':
        this.overlayReference = !this.overlayReference;
        break;
      default:
        break;
    }
  };

  override render() {
    const entry = this.route
      ? phase1Panels.find((p) => p.id === this.route?.model.toLowerCase())
      : undefined;

    if (!entry) {
      return html`
        <div class="stage">
          <div class="empty">Select a panel from the topbar.</div>
        </div>
      `;
    }

    // Tag name follows the convention `<panel-<id>>`. The id is part of the
    // panels registry — controlled, kebab-case input only. Static HTML +
    // unsafeStatic is the documented way to inject a tag name in Lit.
    const tag = unsafeStatic(`panel-${entry.id}`);
    return staticHtml`
      <div class="stage" data-grid=${this.overlayGrid ? 'true' : 'false'}>
        <${tag}></${tag}>
        <div class="meta">${entry.productName} · phase ${entry.phase}</div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'exhibition-panel-view': ExhibitionPanelViewElement;
  }
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target instanceof HTMLInputElement) return true;
  if (target instanceof HTMLTextAreaElement) return true;
  if (target instanceof HTMLSelectElement) return true;
  return target.isContentEditable;
}
